// Search-intent landing pages. One entry per URL, all content lives here so the
// page component stays dumb and every page is consistently structured for SEO.
export const SEO_PAGES = {
  "free-kundli": {
    slug: "free-kundli",
    kicker: "Free · no card needed",
    h1: "Free Kundli Online — Your Vedic Birth Chart in 20 Seconds",
    title: "Free Kundli Online | Instant Vedic Birth Chart & Report — AstroFate",
    description:
      "Generate your free Kundli online in seconds. Accurate Vedic birth chart with Lagna, Rashi, Nakshatra, planetary positions, Dasha timeline and a free AI reading. No login needed.",
    intro:
      "Enter your birth date, time and city and AstroFate builds your complete Vedic Kundli — Lagna chart, Navamsa, planetary positions, yogas, doshas and your Vimshottari Dasha timeline. You get a readable report plus one free AI question about your own chart. No payment, no login to see it.",
    bullets: [
      "Full Kundli: D1 Rashi, D9 Navamsa and D10 Dashamsa charts",
      "Lagna, Sun sign, Moon sign and Nakshatra explained in plain language",
      "Yogas, doshas, combustion and your running Mahadasha",
      "Interactive Life Timeline built from your real Dasha periods",
      "Download as PDF or get it emailed to you",
    ],
    faqs: [
      ["Is the Kundli really free?", "Yes. Generating your Kundli and reading the full report costs nothing and needs no login. Your first AI question about the chart is free too — you only create a free account when you want it saved."],
      ["How accurate is an online Kundli?", "Accuracy depends on your birth time and place. AstroFate uses sidereal (Vedic) calculations with Lahiri ayanamsa, so with a correct birth time the chart matches what a traditional astrologer would cast by hand."],
      ["What if I don't know my exact birth time?", "You can still generate the chart — the Rashi positions stay broadly correct. House and Lagna-sensitive predictions get sharper once you confirm the exact time from your birth certificate or hospital record."],
      ["Do you store my birth details?", "Only to generate and re-open your report. You can delete your account and data at any time from the Account & Privacy page."],
    ],
  },
  "ai-kundli-analysis": {
    slug: "ai-kundli-analysis",
    kicker: "AI interpretation",
    h1: "AI Kundli Analysis — Your Chart, Explained in Plain Language",
    title: "AI Kundli Analysis | AI Astrology Chart Interpretation — AstroFate",
    description:
      "AI Kundli analysis that reads your actual birth chart — Lagna, Dasha, yogas and doshas — and answers your questions in plain language. First question free, no card required.",
    intro:
      "Most astrology apps hand you a generic sun-sign paragraph. AstroFate's AI reads your real chart data — Lagna, planetary placements, yogas, doshas and the Dasha period you are living through — and explains what it actually means for your decisions. Ask follow-up questions like you would ask an astrologer.",
    bullets: [
      "Answers grounded in your chart, never a recycled horoscope",
      "Ask about career, marriage, money, timing or remedies",
      "Chat history saved so your chart context carries forward",
      "English and हिन्दी",
      "First question free — no card required",
    ],
    faqs: [
      ["How is AI astrology different from a horoscope app?", "A horoscope app writes one paragraph for everyone born in a month. AstroFate's AI is given your specific chart — ascendant, planets, houses and current Dasha — and answers only from that."],
      ["Does AI replace a human astrologer?", "No. AI gives you fast, specific clarity on your own chart. For a life-changing decision you can book a live consultation with our astrologer from the same account."],
      ["Can I ask follow-up questions?", "Yes. The chat keeps your chart in context, so you can keep digging into the same topic."],
      ["Is the advice safe?", "AstroFate gives guidance, not guarantees, and never offers medical, legal or financial instructions."],
    ],
  },
  "marriage-astrology": {
    slug: "marriage-astrology",
    kicker: "Marriage & relationships",
    h1: "Marriage Astrology — Timing, Compatibility and Chart Insights",
    title: "Marriage Astrology | Marriage Timing & Chart Insights — AstroFate",
    description:
      "What your Kundli says about marriage: 7th house, Venus and Jupiter, Mangal dosha, and the Dasha periods that favour marriage. Free chart, first reading free.",
    intro:
      "Marriage questions are the ones people ask astrologers most: when, with whom, and why it feels delayed. Your chart holds specific markers — the 7th house and its lord, Venus and Jupiter, Navamsa (D9), Mangal dosha and the Dasha period you are in. AstroFate reads those markers from your own chart and explains them without fear-mongering.",
    bullets: [
      "7th house, its lord and Navamsa (D9) read together",
      "Mangal dosha / Kuja dosha checked properly, with cancellation rules",
      "Dasha and transit windows that support marriage",
      "Partner-qualities indicated by your chart, not generic lists",
      "Full Kundli matching with Ashtakoot score for a specific match",
    ],
    faqs: [
      ["Can astrology predict when I will get married?", "Astrology indicates supportive windows, not fixed dates. AstroFate shows which Dasha and transit periods favour marriage in your chart and why."],
      ["Is Mangal dosha a serious problem?", "Often it is overstated. Many charts have a textbook cancellation. AstroFate checks the cancellation conditions before ever calling a chart afflicted."],
      ["Do we both need to generate Kundlis?", "For matching, yes — both birth details. Each chart is generated free."],
      ["Is this suitable for love marriage questions?", "Yes. The same houses and periods are read, and you can ask the AI directly about an existing relationship."],
    ],
  },
  "career-astrology": {
    slug: "career-astrology",
    kicker: "Career & money",
    h1: "Career Astrology — What Your Chart Says About Work and Timing",
    title: "Career Astrology | Job, Business & Timing From Your Kundli — AstroFate",
    description:
      "Career astrology from your real birth chart: 10th house, Dashamsa (D10), Saturn and Rahu, and the Dasha periods for a job change, promotion or business. Free chart.",
    intro:
      "Should you switch jobs, start up, or sit tight for a year? Your chart has a career signature: the 10th house and its lord, the Dashamsa (D10) chart, Saturn's discipline, Rahu's ambition, and the Mahadasha you are living through. AstroFate reads those and tells you what the phase supports — and what it does not.",
    bullets: [
      "10th house, 10th lord and Dashamsa (D10) read together",
      "Fields and work styles your chart leans towards",
      "Timing windows for a switch, promotion or starting up",
      "Saturn / Rahu phases explained without panic",
      "Life Timeline so you can see the next chapters at a glance",
    ],
    faqs: [
      ["Can my Kundli tell me which career suits me?", "It shows leanings — the kind of work and environment where your chart's strengths compound. It will not name a job title for you."],
      ["Is there a good time to change jobs?", "Some Dasha and transit windows support change far more than others. AstroFate marks those windows in your own timeline."],
      ["Does it cover business vs job?", "Yes — the chart indicators for independent work differ from salaried work, and the AI explains which your chart supports more."],
      ["How soon do I get the reading?", "The chart and report generate in about 20 seconds; your first AI career question is free."],
    ],
  },
  "vedic-birth-chart": {
    slug: "vedic-birth-chart",
    kicker: "Learn your chart",
    h1: "Vedic Birth Chart — How to Read Your Kundli, House by House",
    title: "Vedic Birth Chart Explained | How to Read Your Kundli — AstroFate",
    description:
      "A plain-language guide to the Vedic birth chart: Lagna, 12 houses, planets, Nakshatras, divisional charts and Vimshottari Dasha — with your own free chart alongside.",
    intro:
      "A Vedic birth chart (Kundli or Janam patri) is a map of the sky at the moment you were born, drawn in the sidereal zodiac. Once you know what the Lagna, the twelve houses, the nine planets and your Nakshatra stand for, your own chart stops being cryptic. Generate your chart below and read this guide against it.",
    bullets: [
      "Lagna (ascendant) — the lens the whole chart is read through",
      "The 12 houses — what area of life each one governs",
      "Planets and their natural tendencies, signs and strength",
      "Nakshatras — the 27 lunar mansions and why yours matters",
      "Vimshottari Dasha — how Vedic astrology times events",
    ],
    faqs: [
      ["What is the difference between Vedic and Western astrology?", "Vedic astrology uses the sidereal zodiac (fixed stars) and Nakshatras, and times events through Dasha periods. Western astrology uses the tropical zodiac. Your Sun sign can differ between the two."],
      ["What is a Lagna chart?", "The D1 or Rashi chart, drawn from your ascendant. It is the base chart; divisional charts like D9 and D10 refine specific areas."],
      ["Why does birth time matter so much?", "The ascendant moves roughly one degree every four minutes, so a wrong time rotates all twelve houses and changes the reading."],
      ["Is a North Indian or South Indian chart format better?", "Neither — they are two drawings of the same data. Use whichever you read more easily."],
    ],
  },
  "kundli-matching": {
    slug: "kundli-matching",
    kicker: "Compatibility",
    h1: "Kundli Matching — Ashtakoot Compatibility, Read Honestly",
    title: "Kundli Matching Online | Ashtakoot Guna Milan & Compatibility — AstroFate",
    description:
      "Free Kundli matching online with Ashtakoot Guna Milan score, Mangal dosha check and a plain-language read of what actually matters between two charts.",
    intro:
      "Kundli matching compares two charts across the eight Ashtakoot factors — Varna, Vashya, Tara, Yoni, Graha Maitri, Gana, Bhakoot and Nadi — for a score out of 36. A number alone decides nothing, so AstroFate also explains which factors are strong, which need care, and how the two charts behave together day to day.",
    bullets: [
      "Ashtakoot Guna Milan score out of 36, factor by factor",
      "Mangal dosha checked with its cancellation rules",
      "Both Navamsa (D9) charts compared, not just Moon signs",
      "Where friction is likely and what helps",
      "A detailed matching report you can share with family",
    ],
    faqs: [
      ["What is a good Kundli matching score?", "Above 18 out of 36 is traditionally considered workable and above 24 strong. But a low Bhakoot or Nadi score matters more than the total, which is why the factor breakdown is shown."],
      ["Can a low score be worked with?", "Often yes. Specific doshas have classical cancellations, and the rest is guidance about where to be deliberate — not a verdict on the relationship."],
      ["What details do we need?", "Date, time and city of birth for both people. Both charts generate free."],
      ["Is Mangal dosha a reason to call off a match?", "Not on its own. AstroFate checks whether the dosha is actually active before it says anything about it."],
    ],
  },
};

export const SEO_SLUGS = Object.keys(SEO_PAGES);

export const SEO_NAV = [
  ["/free-kundli", "Free Kundli"],
  ["/ai-kundli-analysis", "AI Kundli Analysis"],
  ["/vedic-birth-chart", "Vedic Birth Chart"],
  ["/marriage-astrology", "Marriage Astrology"],
  ["/career-astrology", "Career Astrology"],
  ["/kundli-matching", "Kundli Matching"],
];
